import { useEffect, useState } from 'react'
import {Truck, Lock, Mail, KeyRound, ShieldAlert, LogOut, ArrowRight} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'

type Mode = 'signin' | 'signup' | 'reset'

function AuthForm() {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (countdown <= 0) return
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(t)
  }, [countdown])

  const sendOtp = async () => {
    setError('')
    if (!email.trim()) {
      setError('Please enter your email first.')
      return
    }
    try {
      await lumi.auth.sendOtp({ email: email.trim(), type: mode === 'reset' ? 'resetPassword' : 'register' })
      setCountdown(60)
      toast.success('Verification code sent to your email.')
    } catch (e: any) {
      setError(e?.message || 'Failed to send verification code.')
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required.')
      return
    }
    setLoading(true)
    try {
      if (mode === 'signin') {
        await lumi.auth.signInWithPassword({ email: email.trim(), password })
      } else if (mode === 'signup') {
        if (!otp.trim()) {
          setError('Please enter the verification code.')
          setLoading(false)
          return
        }
        await lumi.auth.signUpWithPassword({ email: email.trim(), password, otp: otp.trim() })
        await lumi.auth.signInWithPassword({ email: email.trim(), password })
      } else {
        if (!otp.trim()) {
          setError('Please enter the verification code.')
          setLoading(false)
          return
        }
        await lumi.auth.resetPassword({ email: email.trim(), newPassword: password, otp: otp.trim() })
        await lumi.auth.signInWithPassword({ email: email.trim(), password })
      }
    } catch (e: any) {
      setError(e?.message || 'Authentication failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    setError('')
    setOtp('')
  }

  return (
    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl shadow-slate-900/5 border border-slate-200 p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#1E293B] flex items-center justify-center shadow-sm">
          <Truck className="w-6 h-6 text-[#10B981]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0F172A] leading-tight">The Freight Calculator</p>
          <p className="text-xs text-slate-500">Admin Console</p>
        </div>
      </div>

      <h1 className="text-xl font-bold text-[#0F172A] mb-1">
        {mode === 'signin' ? 'Sign in to continue' : mode === 'signup' ? 'Create admin account' : 'Reset your password'}
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        {mode === 'signin'
          ? 'Access the freight leads management dashboard.'
          : mode === 'signup'
          ? 'Verify your email to create an account.'
          : 'Verify your email to set a new password.'}
      </p>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
              required
            />
          </div>
        </div>

        {(mode === 'signup' || mode === 'reset') && (
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">Verification Code</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="6-digit code"
                  className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
                  required
                />
              </div>
              <button
                type="button"
                onClick={sendOtp}
                disabled={countdown > 0}
                className="shrink-0 px-3 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                {countdown > 0 ? `${countdown}s` : 'Send Code'}
              </button>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5">
            {mode === 'reset' ? 'New Password' : 'Password'}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
              required
            />
          </div>
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#10B981] hover:bg-[#0ea472] disabled:opacity-60 text-white text-sm font-semibold py-2.5 transition-colors shadow-sm"
        >
          {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
          {!loading && <ArrowRight className="w-4 h-4" />}
        </button>
      </form>

      <div className="mt-5 flex items-center justify-between text-xs">
        {mode === 'signin' ? (
          <>
            <button onClick={() => switchMode('reset')} className="text-slate-500 hover:text-[#0F172A] font-medium">
              Forgot password?
            </button>
            <button onClick={() => switchMode('signup')} className="text-[#10B981] hover:underline font-semibold">
              Create an account
            </button>
          </>
        ) : (
          <button onClick={() => switchMode('signin')} className="text-slate-500 hover:text-[#0F172A] font-medium">
            Back to sign in
          </button>
        )}
      </div>
    </div>
  )
}

function RestrictedState() {
  const { user } = useAuth()
  return (
    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl shadow-slate-900/5 border border-slate-200 p-8 text-center">
      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
        <ShieldAlert className="w-6 h-6 text-red-500" />
      </div>
      <h1 className="text-lg font-bold text-[#0F172A] mb-1">Access Restricted</h1>
      <p className="text-sm text-slate-500 mb-6">
        Signed in as <span className="font-medium text-[#0F172A]">{user?.userName || user?.email}</span>. This dashboard is
        limited to administrator accounts only.
      </p>
      <button
        onClick={() => lumi.auth.signOut()}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50 px-4 py-2.5 transition-colors"
      >
        <LogOut className="w-4 h-4" />
        Sign Out
      </button>
    </div>
  )
}

interface AdminAuthGateProps {
  children: React.ReactNode
}

function AdminAuthGate({ children }: AdminAuthGateProps) {
  const { isAuthenticated, isAdmin } = useAuth()

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center px-4 py-12">
        <AuthForm />
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center px-4 py-12">
        <RestrictedState />
      </div>
    )
  }

  return <>{children}</>
}

export default AdminAuthGate
